import { CheckCircle, BookOpen } from "lucide-react";

// ============================================================
// Shared sub-components for feature announcement cards
// ============================================================

export function PipelineStep({
  icon,
  label,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  color: "blue" | "amber" | "purple" | "green";
}) {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    purple: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    green: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  };

  return (
    <div
      className={`flex items-center gap-1 rounded-lg px-2.5 py-1.5 font-medium ${colorClasses[color]}`}
    >
      {icon}
      {label}
    </div>
  );
}

export function BenefitItem({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2 text-sm text-purple-800 dark:text-purple-200">
      <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-purple-600 dark:text-purple-400" />
      <span>{text}</span>
    </li>
  );
}

export function StepTypeCard({
  icon,
  title,
  description,
  color,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: "teal" | "emerald" | "cyan" | "blue";
}) {
  const colorClasses = {
    teal: "border-teal-200 bg-white dark:border-teal-700/50 dark:bg-gray-800/80",
    emerald: "border-emerald-200 bg-white dark:border-emerald-700/50 dark:bg-gray-800/80",
    cyan: "border-cyan-200 bg-white dark:border-cyan-700/50 dark:bg-gray-800/80",
    blue: "border-blue-200 bg-white dark:border-blue-700/50 dark:bg-gray-800/80",
  };

  const iconColorClasses = {
    teal: "text-teal-600 dark:text-teal-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
    cyan: "text-cyan-600 dark:text-cyan-400",
    blue: "text-blue-600 dark:text-blue-400",
  };

  return (
    <div className={`rounded-lg border p-2.5 ${colorClasses[color]}`}>
      <div className="mb-1 flex items-center gap-1.5">
        <span className={iconColorClasses[color]}>{icon}</span>
        <h5 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h5>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400">{description}</p>
    </div>
  );
}

export function UseCaseItem({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-gray-100 bg-white p-2 dark:border-gray-700 dark:bg-gray-800/50">
      <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
      <span className="text-xs text-gray-700 dark:text-gray-300">{text}</span>
    </div>
  );
}

export function RuleItem({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-white/60 px-3 py-2 dark:bg-gray-800/40">
      <BookOpen className="h-3.5 w-3.5 shrink-0 text-blue-500" />
      <span className="text-xs font-medium text-blue-800 dark:text-blue-200">{title}</span>
    </div>
  );
}

export function PromptExample({
  icon,
  title,
  prompt,
  color,
}: {
  icon: React.ReactNode;
  title: string;
  prompt: string;
  color: "emerald" | "amber" | "blue" | "purple";
}) {
  const colorClasses: Record<string, string> = {
    emerald:
      "border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300",
    blue:
      "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300",
    purple:
      "border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300",
  };

  const textClasses: Record<string, string> = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    amber: "text-amber-600 dark:text-amber-400",
    blue: "text-blue-600 dark:text-blue-400",
    purple: "text-purple-600 dark:text-purple-400",
  };

  return (
    <div className={`rounded-lg border p-3 ${colorClasses[color]}`}>
      <div className="mb-1 flex items-center gap-1.5 text-xs font-medium">
        {icon}
        {title}
      </div>
      <p className={`text-xs italic ${textClasses[color]}`}>&quot;{prompt}&quot;</p>
    </div>
  );
}
